/*
 * @Author: Ryan
 * @Date: 2025-12-02
 * @Description: How-to section showing upload workflow and multi-language support
 */
import Image from 'next/image';
import React from 'react';

interface StepCardProps {
  index: number;
  titleKey: string;
  descriptionKey: string;
}

const steps = [
  {
    titleKey: 'Step 1: Upload Your Video',
    descriptionKey:
      'Upload from your device, YouTube, or cloud. FalcoCut supports all major formats including MP4, MOV, and AVI.',
  },
  {
    titleKey: 'Step 2: Generate Multilingual Subtitles and AI Dubbing',
    descriptionKey:
      'Choose from 35 languages. Generate subtitles, clone voices, or enable AI dubbing with accurate lip sync and background audio preservation.',
  },
  {
    titleKey: 'Step 3: Translate & Export',
    descriptionKey:
      'Preview your translated video, edit if needed, then export and share. With FalcoCut, your content is ready for global audiences in minutes.',
  },
];

const StepCard: React.FC<StepCardProps> = ({ index, titleKey, descriptionKey }) => {
  return (
    <div className="flex gap-4 rounded-2xl p-4 pad:p-5">
      <div>
        <h3 className="text-base pad:text-lg font-semibold text-gray-900">{titleKey}</h3>
        <p className="text-xs pad:text-sm text-gray-600 mt-1">{descriptionKey}</p>
      </div>
    </div>
  );
};

const HowToTranslateSection: React.FC = () => {
  return (
    <section className="w-full py-[90px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        <h2 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-gray-900 mt-4 mb-4 text-center">
          How to Translate a Video with FalcoCut
        </h2>

        <div className="max-w-[1010px] mx-auto flex flex-col sm:flex-row items-center gap-8 pad:gap-10 sm:gap-12">
          {/* Visual */}
          <div className="flex-1">
            <div className="rounded-2xl overflow-hidden">
              <Image
                src="/images/landing-how-to-translate-a-video-with-falcocut.png"
                alt="How to Translate a Video with FalcoCut"
                width={440}
                height={260}
                className="w-full h-auto object-cover"
                loading="lazy"
              />
            </div>
          </div>
          {/* Steps */}
          <div className="flex-1 w-full">
            <div className="space-y-5">
              {steps.map((step, index) => (
                <StepCard
                  key={step.titleKey}
                  index={index + 1}
                  titleKey={step.titleKey}
                  descriptionKey={step.descriptionKey}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowToTranslateSection;
